import type { GameStateData } from '../game/GameState.js';
import { hasLegalPlay } from '../game/MoveValidator.js';
import { tileEquals as tilesEqual } from '../game/types.js';
import type { DominoRuleSet } from '../game/variants/types.js';
import type { Evaluator } from './Evaluator.js';
import { fallback, pickEasy, pickMedium } from './greedyTiers.js';
import { IsmctsSearcher } from './ismcts/IsmctsSearcher.js';
import { KozyolEvaluator } from './KozyolEvaluator.js';
import type { MoveEnumerator, PlayMove } from './MoveEnumerator.js';
import type { AiAction, AiChoice, MoveScorer } from './types.js';

export interface DifficultyDependencies {
  readonly enumerator: MoveEnumerator;
  /** Fives evaluator. Penalty (Козёл) states use a `KozyolEvaluator` instead. */
  readonly evaluator: Evaluator;
  /** Seedable RNG for tests. Defaults to `Math.random`. */
  readonly rng?: () => number;
  /**
   * Optional override for the Hard tier: caller can plug in a different
   * searcher (e.g. a Web Worker bridge). Defaults to an in-process
   * `IsmctsSearcher` with a small iteration budget.
   */
  readonly hardSearcher?: HardSearcher;
  /** Rule set passed to the default in-process searcher. */
  readonly ruleSet?: DominoRuleSet;
}

/**
 * Pluggable Hard-tier search interface. The default is in-process; the
 * browser wiring (M7.4) can swap in a Web Worker bridge that implements the
 * same shape asynchronously and waits on it via a separate code path.
 */
export interface HardSearcher {
  search(state: GameStateData, seat: number): { action: AiAction; iterations: number };
}

/** Default in-process Hard-tier iteration budget. Tuned for ~150–250 ms. */
const DEFAULT_HARD_ITERATIONS = 1500;
const DEFAULT_HARD_DEADLINE_MS = 800;

export type Difficulty = 'easy' | 'medium' | 'hard';

/**
 * Translates a difficulty tier into a concrete AI action. Each tier is a
 * distinct strategy:
 *
 * - **easy**: pure heuristic — H1 (immediate score) + H2 (heaviness) + noise.
 *   No evaluator, no lookahead. Penalty (Козёл) drops H1: nothing scores in play.
 * - **medium**: full evaluator + 1-ply lookahead. Fives uses the 9-feature
 *   `Evaluator`; penalty scoring uses `KozyolEvaluator` (see `evaluatorFor`).
 * - **hard**: SO-IS-MCTS via [[ismctssearcher]]. Samples opponent hands from
 *   a Bayesian belief tracker per iteration; Medium-tier softmax rollouts.
 *   Falls back to Medium for any non-play search output (deadline-misses).
 *
 * All tiers handle `draw` and `pass` as fallback actions when no play is
 * possible — the caller (`AiPlayer`) re-invokes after `applyDraw` so the
 * tier doesn't need to chain draws internally.
 */
export class DifficultyStrategy {
  private hardSearcherCache: HardSearcher | null = null;
  private kozyolEvaluatorCache: KozyolEvaluator | null = null;

  constructor(private readonly deps: DifficultyDependencies) {}

  pickAction(state: GameStateData, seat: number, difficulty: Difficulty): AiChoice {
    // Pending Muggins — if we are the scorer, lock in the points immediately.
    // (Other seats stealing in hot-seat is M4-deferred per the plan.)
    // Penalty scoring never opens a Muggins window; the guard keeps it that way.
    if (
      state.ruleSet.scoring === 'fives' &&
      state.pendingMuggins &&
      state.pendingMuggins.scorerSeat === seat
    ) {
      return { action: { kind: 'claim-muggins' }, score: null, depth: 0 };
    }
    const hand = state.hands[seat] ?? [];
    if (hand.length === 0 || !hasLegalPlay(state.chain, hand, state.forcedLeadTile)) {
      return fallback(state);
    }
    const moves = this.deps.enumerator.enumerate(state.chain, hand, state.forcedLeadTile);
    if (moves.length === 0) return fallback(state);

    switch (difficulty) {
      case 'easy':
        return pickEasy(moves, this.deps.rng ?? Math.random, state.ruleSet.scoring === 'penalty');
      case 'medium':
        return pickMedium(state, seat, moves, this.evaluatorFor(state));
      case 'hard':
        return this.pickHard(state, seat, moves);
    }
  }

  private getHardSearcher(state: GameStateData): HardSearcher {
    if (this.deps.hardSearcher) return this.deps.hardSearcher;
    if (this.hardSearcherCache !== null) return this.hardSearcherCache;
    const ruleSet = this.deps.ruleSet ?? state.ruleSet;
    this.hardSearcherCache = new IsmctsSearcher({
      ruleSet,
      iterations: DEFAULT_HARD_ITERATIONS,
      deadlineMs: DEFAULT_HARD_DEADLINE_MS,
      rng: this.deps.rng,
    });
    return this.hardSearcherCache;
  }

  /**
   * Hard: SO-IS-MCTS. The searcher samples opponent hands per iteration
   * from a Bayesian belief tracker and runs UCB1 selection / Medium-tier
   * softmax rollouts in a tree shared across worlds. Falls back to Medium
   * if the search returns a non-play action when plays were available.
   */
  private pickHard(state: GameStateData, seat: number, moves: readonly PlayMove[]): AiChoice {
    const searcher = this.getHardSearcher(state);
    const { action, iterations } = searcher.search(state, seat);
    if (action.kind === 'play') {
      const match = moves.find(
        (m) => tilesEqual(m.tile, action.tile) && m.armIndex === action.armIndex,
      );
      if (match) {
        return { action: { kind: 'play', tile: match.tile, armIndex: match.armIndex }, score: iterations, depth: 2 };
      }
    }
    // Searcher emitted a non-play action despite plays being available — e.g.
    // exhausted the deadline before any iteration completed. Use Medium as a
    // safe fallback so we never return an illegal move.
    return pickMedium(state, seat, moves, this.evaluatorFor(state));
  }

  /** Play evaluator for `state`'s scoring: the injected fives one, or Козёл's. */
  evaluatorFor(state: GameStateData): MoveScorer {
    if (state.ruleSet.scoring !== 'penalty') return this.deps.evaluator;
    if (this.kozyolEvaluatorCache === null) {
      this.kozyolEvaluatorCache = new KozyolEvaluator(state.ruleSet);
    }
    return this.kozyolEvaluatorCache;
  }
}
